import { useGetMonitorDeploymentStats } from "@/api/strategyApi";
import { Card, CardHeader } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart";
import { useUserContext } from "@/contexts/userContext";
import {
  DP02,
  minFixedDecimal,
  toNumberOrNull,
} from "@/helpers/helperFunctions";
import { AccentColor } from "@/helpers/types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, XAxis, Cell } from "recharts";
import { NameType } from "recharts/types/component/DefaultTooltipContent";
import { ContentType } from "recharts/types/component/Tooltip";
import { ValueType } from "tailwindcss/types/config";

// Define the type for chart data points
type ChartDataPoint = {
  timestamp: string; // Formatted time
  slipage: number; // Slippage value
};

const CustomTooltip: ContentType<ValueType, NameType> = ({
  active,
  payload,
}) => {
  if (active && payload && payload.length) {
    const val = toNumberOrNull(payload[0]?.payload?.slipage);
    return (
      <div className="flex flex-col gap-2 text-sm bg-background text-foreground border rounded shadow-sm p-2">
        <div className="flex gap-1">
          {"Slipage"}:
          <span
            className={
              val == null
                ? ""
                : val < 0
                ? "text-red-foreground"
                : "text-green-foreground"
            }
          >
            ${minFixedDecimal(val, DP02, "N/A")}
          </span>
        </div>
        <div className="">{payload[0]?.payload?.tooltipTimestamp}</div>
      </div>
    );
  }

  return null;
};

const SlipageChart = () => {
  const { user } = useUserContext();
  const { t: ct } = useTranslation("common");

  // Fetch deployment stats using the custom API hook
  const {
    data: deploymentData,
    isSuccess,
    isLoading,
  } = useGetMonitorDeploymentStats(user?.user_id);

  // State to store the processed chart data
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

  // Effect to process slippage data when API call is successful
  useEffect(() => {
    if (!isSuccess || !deploymentData?.data?.slippage_dict) return;

    setChartData(
      Object.entries(deploymentData.data.slippage_dict).map(
        ([timestamp, slippage]) => {
          const date = new Date(Number(timestamp) * 1000);

          const formattedDate = `${date.getDate()}-${date.toLocaleString(
            "en-GB",
            { month: "short" }
          )}-${date.getFullYear().toString().slice(-2)}`;

          const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          });
          return {
            timestamp: new Date(Number(timestamp) * 1000).toLocaleTimeString(
              "en-US",
              {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              }
            ),
            slipage: Number(slippage), // Ensuring it's a number
            tooltipTimestamp: `${formattedDate} ${formattedTime}`,
          };
        }
      )
    );
  }, [isSuccess, deploymentData]);

  // Chart configuration settings
  const chartConfig = {
    desktop: {
      label: "Time",
      color: "#2563eb",
    },
  } satisfies ChartConfig;

  // Return empty fragment if no data is available
  if (chartData.length == 0) return <></>;

  return (
    <div className="flex min-h-52 h-52">
      {/* Card container for the chart */}
      <Card className="pb-8 flex flex-col w-full px-4">
        <CardHeader className="py-2 px-0 text-sm font-bold text-left tracking-tight w-full">
          <div className="text-sm  text-muted-foreground">
            {ct("Slippage per order")}
          </div>
        </CardHeader>

        {/* Chart container with custom styling */}
        <ChartContainer
          config={chartConfig}
          className="max-h-[200px] h-full w-full pb-6"
        >
          {/* BarChart component to display slippage data */}
          <BarChart accessibilityLayer data={chartData}>
            <CartesianGrid vertical={false} />{" "}
            {/* Tooltip for displaying slippage and time on hover */}
            <ChartTooltip content={CustomTooltip} />
            {/* Grid lines for readability */}
            <XAxis
              dataKey="timestamp" // Display formatted time on X-axis
              tickLine={false}
              tickMargin={10}
              axisLine={false}
            />
            <Bar dataKey="slipage" type="linear" fill="white">
              {chartData.map((entry, index) => {
                const val = toNumberOrNull(entry.slipage, 0);
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      val < 0
                        ? AccentColor.RED
                        : val > 0
                        ? AccentColor.GREEN
                        : ""
                    }
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ChartContainer>
      </Card>
    </div>
  );
};

export default SlipageChart;
